import React, { useState } from 'react';
import { CheckCircle2, Clock, Loader2, Mail, Send, AlertTriangle, FileCheck2 } from 'lucide-react';
import { useAuth } from '../../../src/contexts/AuthContext';
import { useToast } from '../../../src/contexts/ToastContext';
import { Rateio, RateioItem, RateioUnidadeGeradora } from '../../../types';
import { aprovarRateio, enviarPlanilhaProtocolo, marcarProtocoladoManual, marcarConciliado } from './protocoloActions';
import ReportarFalhaModal from './ReportarFalhaModal';
import EnviarProtocoloModal from './EnviarProtocoloModal';

interface RateioStatusWorkflowProps {
  rateio: Rateio;
  ug: RateioUnidadeGeradora;
  itens: RateioItem[];
  onChanged: () => void;
}

const STATUS_BADGE: Record<Rateio['status'], { label: string; className: string; icon: React.ReactNode }> = {
  rascunho: { label: 'Rascunho', className: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300', icon: <Clock size={14} /> },
  aguardando_protocolo: { label: 'Aguardando Protocolo', className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300', icon: <Clock size={14} /> },
  protocolado: { label: 'Protocolado', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300', icon: <Mail size={14} /> },
  conciliado: { label: 'Conciliado', className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300', icon: <CheckCircle2 size={14} /> },
};

const RateioStatusWorkflow: React.FC<RateioStatusWorkflowProps> = ({ rateio, ug, itens, onChanged }) => {
  const { profile } = useAuth();
  const { addToast } = useToast();
  const [loadingAction, setLoadingAction] = useState<string | null>(null);
  const [showReportarFalha, setShowReportarFalha] = useState(false);
  const [showEnviarProtocolo, setShowEnviarProtocolo] = useState(false);

  const isGestor = profile?.role === 'adm' || profile?.role === 'gerente';
  const badge = STATUS_BADGE[rateio.status];

  const runAction = async (action: string, fn: () => Promise<void>, successMsg: string) => {
    setLoadingAction(action);
    try {
      await fn();
      addToast(successMsg, 'success');
      onChanged();
    } catch (error: any) {
      console.error(`Error running rateio action ${action}`, error);
      addToast(error?.message || 'Erro ao processar ação.', 'error');
    } finally {
      setLoadingAction(null);
    }
  };

  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-t border-gray-100 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/80 backdrop-blur">
      <div className="flex items-center gap-3">
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${badge.className}`}>
          {badge.icon} {badge.label}
        </span>
        {rateio.status === 'protocolado' && rateio.protocolado_em && (
          <span className="text-xs text-gray-500 dark:text-gray-400">
            em {new Date(rateio.protocolado_em).toLocaleDateString('pt-BR')} via{' '}
            {rateio.protocolado_via === 'email' ? 'e-mail automático' : 'marcação manual'}
          </span>
        )}
        {rateio.historico_falhas?.length > 0 && (
          <span className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
            <AlertTriangle size={12} /> {rateio.historico_falhas.length} falha(s) reportada(s)
          </span>
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {rateio.status === 'rascunho' && isGestor && (
          <button
            onClick={() =>
              runAction('aprovar', () => aprovarRateio(rateio.id), 'Rateio aprovado — aguardando protocolo.')
            }
            disabled={loadingAction !== null}
            className="px-4 py-2 rounded-lg bg-primary text-white font-medium hover:opacity-90 disabled:opacity-60 flex items-center gap-2 text-sm"
          >
            {loadingAction === 'aprovar' ? <Loader2 size={16} className="animate-spin" /> : <FileCheck2 size={16} />}
            Aprovar Rateio
          </button>
        )}

        {rateio.status === 'aguardando_protocolo' && isGestor && (
          <>
            <button
              onClick={() => setShowEnviarProtocolo(true)}
              disabled={loadingAction !== null}
              className="px-4 py-2 rounded-lg bg-primary text-white font-medium hover:opacity-90 disabled:opacity-60 flex items-center gap-2 text-sm"
            >
              <Send size={16} />
              Enviar Planilha
            </button>
            <button
              onClick={() =>
                runAction(
                  'protocolar-manual',
                  () => marcarProtocoladoManual(rateio.id),
                  'Rateio marcado como protocolado.'
                )
              }
              disabled={loadingAction !== null}
              className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-60 flex items-center gap-2 text-sm"
            >
              {loadingAction === 'protocolar-manual' && <Loader2 size={16} className="animate-spin" />}
              Marcar como Protocolado
            </button>
          </>
        )}

        {rateio.status === 'protocolado' && (
          <>
            <button
              onClick={() => setShowReportarFalha(true)}
              disabled={loadingAction !== null}
              className="px-4 py-2 rounded-lg border border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-400 font-medium hover:bg-amber-50 dark:hover:bg-amber-900/20 disabled:opacity-60 flex items-center gap-2 text-sm"
            >
              <AlertTriangle size={16} /> Reportar Falha
            </button>
            {isGestor && (
              <button
                onClick={() =>
                  runAction('conciliar', () => marcarConciliado(rateio.id), 'Rateio marcado como conciliado.')
                }
                disabled={loadingAction !== null}
                className="px-4 py-2 rounded-lg bg-primary text-white font-medium hover:opacity-90 disabled:opacity-60 flex items-center gap-2 text-sm"
              >
                {loadingAction === 'conciliar' ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                Marcar como Conciliado
              </button>
            )}
          </>
        )}
      </div>

      {showReportarFalha && (
        <ReportarFalhaModal
          rateioId={rateio.id}
          onClose={() => setShowReportarFalha(false)}
          onReportado={() => {
            setShowReportarFalha(false);
            onChanged();
          }}
        />
      )}

      {showEnviarProtocolo && (
        <EnviarProtocoloModal
          ugNome={ug.nome}
          onClose={() => setShowEnviarProtocolo(false)}
          onConfirmar={async (observacao, emailCopia) => {
            await runAction(
              'enviar',
              async () => {
                const resultado = await enviarPlanilhaProtocolo({ ug, rateio, itens, observacao, emailCopia });
                if (!resultado.enviado) {
                  addToast('Planilha baixada — envie manualmente e clique em "Marcar como Protocolado".', 'info');
                }
              },
              'Planilha enviada — rateio protocolado.'
            );
            setShowEnviarProtocolo(false);
          }}
        />
      )}
    </div>
  );
};

export default RateioStatusWorkflow;
